import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// V1.37.3 · Hook para Google reCAPTCHA (v2 checkbox o v3 invisible).
// Fail-safe: si el script falla al cargar o Google no responde,
// `execute()` devuelve string vacía y el backend hace fail-open.

const SCRIPT_ID = "google-recaptcha-script";

async function fetchPublicConfig() {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const { data } = await axios.get(`${base}/api/auth/recaptcha/public-config`, { timeout: 4000 });
    return data;
  } catch {
    return { enabled: false, site_key: "", version: "v2" };
  }
}

function loadScript(version, siteKey) {
  return new Promise((resolve) => {
    if (window.grecaptcha && (version === "v2" ? window.grecaptcha.render : window.grecaptcha.execute)) {
      return resolve(true);
    }
    if (document.getElementById(SCRIPT_ID)) {
      const t = setInterval(() => {
        if (window.grecaptcha) { clearInterval(t); resolve(true); }
      }, 200);
      setTimeout(() => { clearInterval(t); resolve(!!window.grecaptcha); }, 6000);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    // v2: script sin ?render= carga el widget "I'm not a robot"
    // v3: script con ?render=SITE_KEY carga el motor invisible
    s.src = version === "v3"
      ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
      : `https://www.google.com/recaptcha/api.js?render=explicit`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => resolve(true));
      } else { resolve(true); }
    };
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
}

export function useRecaptcha() {
  const [config, setConfig] = useState({ enabled: false, site_key: "", version: "v2" });
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [v2Token, setV2Token] = useState("");   // v2: rellenado por el widget
  const [v2WidgetId, setV2WidgetId] = useState(null);
  const containerRef = useRef(null);            // v2: nodo donde renderiza el checkbox
  const loadedKeyRef = useRef("");

  // Inicialización + carga de script
  useEffect(() => {
    (async () => {
      const cfg = await fetchPublicConfig();
      setConfig(cfg);
      if (!cfg.enabled || !cfg.site_key) {
        setReady(true);
        return;
      }
      if (loadedKeyRef.current === cfg.site_key && window.grecaptcha) {
        setReady(true);
        return;
      }
      const ok = await loadScript(cfg.version || "v2", cfg.site_key);
      loadedKeyRef.current = cfg.site_key;
      setLoadError(!ok);
      setReady(true);
    })();
  }, []);

  // v2: render explícito del widget cuando el contenedor está disponible
  useEffect(() => {
    if (!ready || !config.enabled || config.version !== "v2") return;
    if (!containerRef.current || v2WidgetId !== null) return;
    if (!window.grecaptcha || !window.grecaptcha.render) return;
    try {
      const id = window.grecaptcha.render(containerRef.current, {
        sitekey: config.site_key,
        callback: (t) => setV2Token(t || ""),
        "expired-callback": () => setV2Token(""),
        "error-callback": () => setV2Token(""),
      });
      setV2WidgetId(id);
    } catch (err) {
      console.warn("recaptcha v2 render failed:", err);
    }
  }, [ready, config, v2WidgetId]);

  const reset = useCallback(() => {
    setV2Token("");
    if (v2WidgetId !== null && window.grecaptcha?.reset) {
      try { window.grecaptcha.reset(v2WidgetId); } catch { /* ignore */ }
    }
  }, [v2WidgetId]);

  /** Devuelve el token actual a enviar al backend.
   *  - v2: el que el usuario ya generó al marcar el checkbox.
   *  - v3: se ejecuta on-demand para la acción indicada. */
  const execute = useCallback(async (action = "login") => {
    if (!config.enabled || !config.site_key) return "";
    if (config.version === "v2") {
      return v2Token || "";
    }
    // v3
    if (!window.grecaptcha || !window.grecaptcha.execute) return "";
    try {
      const token = await Promise.race([
        window.grecaptcha.execute(config.site_key, { action }),
        new Promise((res) => setTimeout(() => res(""), 4000)),
      ]);
      return token || "";
    } catch {
      return "";
    }
  }, [config, v2Token]);

  return {
    ready,
    enabled: !!(config.enabled && config.site_key),
    version: config.version || "v2",
    siteKey: config.site_key,
    loadError,
    execute,
    reset,
    // v2 helpers
    v2ContainerRef: containerRef,
    v2Token,
  };
}
